import type { FieldDefinition } from "@estelle/domain";

/**
 * Which columns a list shows, and in what order.
 *
 * An ordered `string[]` of visible field names is the whole state — no
 * separate hidden list, no per-column width or pin. That keeps it exactly
 * as URL-friendly as the filter state from #39: a flat array of stable
 * names round-trips through a comma-joined query param with no encoding
 * beyond that, and a saved view stores it the same way it stores a filter.
 * Width, pinning, and the like are features nobody has asked for yet and
 * would have to be designed, not derived from `FieldDefinition` — adding
 * them later means widening this type, not replacing it.
 */
export type ColumnState = string[];

/**
 * The starting set: every field, in the order the module declares them —
 * except one marked `defaultHidden` (#354), which stays out of the default
 * view and in the chooser only.
 *
 * All rather than the first six, by decision on 2026-09-15: a list that opens
 * with columns hidden sends people to the chooser to find data they assumed
 * was missing. A wide list scrolls sideways inside its own container, and
 * anyone who wants fewer columns hides them. `defaultHidden` is the narrow
 * exception: a field worth offering but not worth the row height it costs
 * every row, such as `tags`' pills.
 *
 * Declaration order still matters: `fields.ts` lists identifying fields
 * first, so they are the leftmost columns and the ones still on screen before
 * a wide list scrolls.
 */
export function defaultColumns(fields: FieldDefinition[]): string[] {
  return fields
    .filter((field) => !field.defaultHidden && field.displayable !== false)
    .map((field) => field.name);
}

/**
 * Resolve `state` into the ordered fields a table should render.
 *
 * A name in `state` that no longer matches a field — a saved view written
 * before a field was renamed or removed — is dropped rather than surfaced
 * as an error. The alternative, rendering a blank or placeholder column,
 * exposes an internal rename to every user still holding the old view for
 * no benefit. If dropping stale names empties the list entirely, that
 * view has nothing left to show, so we fall back to `defaultColumns`
 * rather than rendering a table with zero columns.
 */
export function applyColumns(
  fields: FieldDefinition[],
  state: ColumnState,
): FieldDefinition[] {
  // A filter-only field has no column, whatever a stale preference or link
  // says: it is not in the map, so it is dropped like any unknown name.
  const byName = new Map(
    fields
      .filter((field) => field.displayable !== false)
      .map((field) => [field.name, field]),
  );

  const resolved: FieldDefinition[] = [];
  for (const name of state) {
    const field = byName.get(name);
    if (field) resolved.push(field);
  }
  if (resolved.length > 0) return resolved;

  const fallback: FieldDefinition[] = [];
  for (const name of defaultColumns(fields)) {
    const field = byName.get(name);
    if (field) fallback.push(field);
  }
  return fallback;
}

/**
 * Show or hide `name`, appending a newly shown field at the end of the
 * visible order and otherwise leaving the existing order untouched.
 *
 * Hiding the last visible column is refused here rather than left to the
 * UI to prevent: a table with zero columns has nothing to click to bring
 * one back except "Reset to default", and enforcing the guard in the pure
 * function means no future caller — a keyboard shortcut, a bulk action —
 * can reintroduce that state by skipping whatever UI check the chooser
 * happens to have.
 */
export function toggleColumn(state: ColumnState, name: string): ColumnState {
  const isVisible = state.includes(name);
  if (!isVisible) return [...state, name];
  if (state.length <= 1) return state;
  return state.filter((visibleName) => visibleName !== name);
}

/**
 * Move the column named `name` one place earlier or later.
 *
 * A no-op at either end rather than clamping or wrapping: the up button
 * on the first row and the down button on the last row are disabled in
 * the chooser, but the helper stays correct even if a caller doesn't
 * check first, and "wrap to the other end" is a surprising thing for a
 * reorder button to do.
 */
export function moveColumn(
  state: ColumnState,
  name: string,
  direction: "up" | "down",
): ColumnState {
  const index = state.indexOf(name);
  if (index === -1) return state;

  const target = direction === "up" ? index - 1 : index + 1;
  if (target < 0 || target >= state.length) return state;

  const current = state[index];
  const other = state[target];
  if (current === undefined || other === undefined) return state;

  const next = [...state];
  next[index] = other;
  next[target] = current;
  return next;
}

/**
 * The URL form: names joined by commas.
 *
 * Column names are stable keys chosen by whoever declared the field, so they
 * need no encoding of their own. Returning undefined for the default set
 * keeps `?cols=` out of the address bar until someone actually changes
 * something.
 */
export function encodeColumns(
  state: ColumnState,
  fields: FieldDefinition[],
): string | undefined {
  const fallback = defaultColumns(fields);
  const isDefault =
    state.length === fallback.length &&
    state.every((name, index) => fallback[index] === name);
  return isDefault ? undefined : state.join(",");
}

/** Read a stored column list back. Absent or empty means the default set. */
export function decodeColumns(
  raw: string | undefined,
  fields: FieldDefinition[],
): ColumnState {
  if (!raw) return defaultColumns(fields);
  const names = raw.split(",").filter((name) => name !== "");
  // A URL naming only fields that no longer exist is a stale link, not a
  // request for an empty table. applyColumns already falls back, but doing it
  // here keeps the state itself honest rather than only its rendering.
  const known = new Set(
    fields
      .filter((field) => field.displayable !== false)
      .map((field) => field.name),
  );
  const kept = names.filter((name) => known.has(name));
  return kept.length > 0 ? kept : defaultColumns(fields);
}

const storageKey = (scope: string) => `estelle.columns.${scope}`;

/**
 * The columns this browser last chose for a list.
 *
 * Per list and per browser, not in the URL (moved 2026-09-15): which columns
 * someone reads is their own presentation, and with every column shown by
 * default the list made links hundreds of characters long without changing
 * which rows anyone would see. Filter and sort stay in the URL because those
 * do.
 *
 * `null` when this browser has chosen nothing, so the caller can fall back to
 * the scope's default view before the module's default set.
 *
 * Wrapped because `localStorage` throws outright in a browser set to block
 * site data, and a preference is not worth a list that fails to render.
 */
export function readColumnPreference(
  scope: string,
  fields: FieldDefinition[],
): ColumnState | null {
  try {
    const raw = localStorage.getItem(storageKey(scope));
    return raw ? decodeColumns(raw, fields) : null;
  } catch {
    return null;
  }
}

/** Store the default as nothing, so a later change to the default applies. */
export function writeColumnPreference(
  scope: string,
  state: ColumnState,
  fields: FieldDefinition[],
): void {
  try {
    const encoded = encodeColumns(state, fields);
    if (encoded === undefined) {
      localStorage.removeItem(storageKey(scope));
    } else {
      localStorage.setItem(storageKey(scope), encoded);
    }
  } catch {
    // Unwritable storage costs the preference, not the change on screen.
  }
}

/**
 * Move the column named `name` to sit at `index`.
 *
 * What a drag expresses, which `moveColumn` cannot: dragging a column six
 * places is one gesture, and expressing it as six swaps would write six
 * URL updates and six history entries for one movement.
 *
 * Removing before inserting is what makes `index` mean "where it ends up"
 * rather than "where it was dropped relative to its old self" — without it,
 * dragging a column downward lands it one place short every time.
 */
export function moveColumnTo(
  state: ColumnState,
  name: string,
  index: number,
): ColumnState {
  const from = state.indexOf(name);
  if (from === -1) return state;

  const without = state.filter((visibleName) => visibleName !== name);
  const target = Math.max(0, Math.min(index, without.length));
  return [...without.slice(0, target), name, ...without.slice(target)];
}
